import {IStorage, IStorageOptions} from "./storage.interface";
import {
    NullableString,
    UndefinedString
} from "../../types/nullable-types";


export interface EmbeddedStorageOptions<TParentStorageKey extends string> {
    parentStorage: IStorage<TParentStorageKey>;
    parentStorageKey: TParentStorageKey;
    getStorageDiscriminator: () => string;
}

export class EmbeddedStorage<TKey extends string, TParentStorageKey extends string> implements IStorage<TKey> {

    constructor(private readonly options?: EmbeddedStorageOptions<TParentStorageKey>) {

        if(options) {
            this._storageData = options.parentStorage.getJson(options.parentStorageKey, {
                discriminator: options.getStorageDiscriminator()
            }) ?? {};
        } else {
            this._storageData = {};
        }
    }

    private _storageData: Record<string, any> = {};


    protected _composeKey(key: TKey, discriminator: UndefinedString): string {
        if(discriminator) {
            return `${key}.${discriminator}`;
        } else {
            return key;
        }
    }

    private _persistData(): void {
        if(this.options) {
            this.options.parentStorage.setJson(this.options.parentStorageKey, this._storageData, {
                discriminator: this.options.getStorageDiscriminator()
            });
        }

    }

    setItem(key: TKey, value: string, options?: IStorageOptions): void {
        this._storageData[this._composeKey(key, options?.discriminator)] = value;
        this._persistData();
    }

    getItem(key: TKey, options?: IStorageOptions): NullableString {
        return this._storageData[this._composeKey(key, options?.discriminator)] ?? null;
    }

    setJson(key: TKey, value: any,options?: IStorageOptions): void {
        if(value) {
            this.setItem(key, value, options);
        } else {
            this.removeItem(key, options);
        }
    }
    getJson<TValue = any>(key: TKey, options?: IStorageOptions): TValue | null {
        return this.getItem(key, options) as TValue;

    }
    removeItem(key: TKey, options?: IStorageOptions): void {
        const composedKey = this._composeKey(key, options?.discriminator);
        if(this._storageData[composedKey]) {
            delete this._storageData[composedKey];
            this._persistData();
        }
    }

    dispose() {
        if(this.options) {
            this.options.parentStorage.removeItem(this.options.parentStorageKey, {
                discriminator: this.options.getStorageDiscriminator()
            });
        }
        this._storageData = {};
    }

    reset(): void {
        this._storageData = {};
        this._persistData();
    }

}